import { http } from '../lib/api/http';
import { uploadFileWithPresign } from './uploadsApi';
import { getSessionUserId } from './sessionUser';

export const COMPANY_DOCUMENT_STATUS = {
  NONE: 'none',
  PENDING: 'pending',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
};

const DEFAULT_EXPAND = 'submitted_by,reviewed_by';
const BASE_PATH = '/company-documents';

function normalizeCompanyDocument(document) {
  if (!document || typeof document !== 'object') {
    return document;
  }

  return {
    ...document,
    companyId: document.company_id || document.companyId,
    documentKey: document.document_key || document.documentKey,
    fileKey: document.file_key || document.fileKey,
    fileUrl: document.file_url || document.fileUrl,
    file: document.file_url || document.file,
    submittedBy: document.submitted_by || document.submittedBy,
    reviewedBy: document.reviewed_by || document.reviewedBy,
    reviewedAt: document.reviewed_at || document.reviewedAt,
    createdAt: document.created_at || document.createdAt,
    updatedAt: document.updated_at || document.updatedAt,
    status: document.status || COMPANY_DOCUMENT_STATUS.PENDING,
  };
}

export function getCompanyDocumentFileUrl(document) {
  if (!document) return '';
  return document.fileUrl || document.file_url || '';
}

export function mapDocumentsByKey(documents = []) {
  return documents.reduce((accumulator, document) => {
    if (document?.documentKey) {
      accumulator[document.documentKey] = document;
    }
    return accumulator;
  }, {});
}

export async function listCompanyDocuments(companyId, { expand = true } = {}) {
  if (!companyId) return [];
  const params = { company_id: companyId };
  if (expand) {
    params.expand = DEFAULT_EXPAND;
  }
  const response = await http.get(BASE_PATH, { params });
  const raw = Array.isArray(response.data) ? response.data : [];
  return raw.map(normalizeCompanyDocument);
}

export async function fetchCompanyDocumentByKey(
  companyId,
  documentKey,
  { expand = true } = {}
) {
  if (!companyId || !documentKey) {
    return null;
  }

  const docs = await listCompanyDocuments(companyId, { expand });
  const found = docs.find((doc) => doc.document_key === documentKey || doc.documentKey === documentKey);
  return normalizeCompanyDocument(found) || null;
}

function buildDocumentPayload({
  companyId,
  documentKey,
  title,
  notes,
  submittedBy,
  presign,
}) {
  const payload = {
    company_id: companyId,
    document_key: documentKey,
    status: COMPANY_DOCUMENT_STATUS.PENDING,
    title: title || '',
    notes: notes || '',
    file_key: presign?.object_key || '',
    file_url: presign?.public_url || '',
  };

  if (submittedBy) {
    payload.submitted_by = submittedBy;
  }

  return payload;
}

export async function uploadCompanyDocument({
  companyId,
  documentKey,
  file,
  title = '',
  notes = '',
  submittedBy,
}) {
  if (!companyId) {
    throw new Error('Falta el identificador de la empresa.');
  }
  if (!documentKey) {
    throw new Error('Falta la clave del documento.');
  }
  if (!(file instanceof File)) {
    throw new Error('Selecciona un archivo valido para subir.');
  }

  const existing = await fetchCompanyDocumentByKey(companyId, documentKey, {
    expand: false,
  });

  const presign = await uploadFileWithPresign({ kind: 'document', file });
  const submitterId = submittedBy || (await getSessionUserId());
  const payload = buildDocumentPayload({
    companyId,
    documentKey,
    title,
    notes,
    submittedBy: submitterId,
    presign,
  });

  if (existing?.id) {
    const response = await http.patch(`${BASE_PATH}/${existing.id}`, payload);
    return normalizeCompanyDocument(response.data);
  }

  const response = await http.post(BASE_PATH, payload);
  return normalizeCompanyDocument(response.data);
}

export async function updateCompanyDocument(id, payload) {
  if (!id) {
    throw new Error('Falta el identificador del documento.');
  }
  if (!payload || typeof payload !== 'object') {
    throw new Error('No hay cambios por aplicar al documento.');
  }
  const response = await http.patch(`${BASE_PATH}/${id}`, payload);
  return normalizeCompanyDocument(response.data);
}

export async function setCompanyDocumentStatus(
  id,
  status,
  { reviewerId, reviewedAt = new Date() } = {}
) {
  if (!id) {
    throw new Error('Falta el identificador del documento.');
  }
  if (!status) {
    throw new Error('Selecciona un estatus valido.');
  }

  const updatePayload = {
    status,
  };

  if (status === COMPANY_DOCUMENT_STATUS.VERIFIED) {
    const reviewer = reviewerId || (await getSessionUserId());
    if (reviewer) {
      updatePayload.reviewedBy = reviewer;
    }
    updatePayload.reviewedAt = reviewedAt.toISOString();
  } else {
    updatePayload.reviewedBy = null;
    updatePayload.reviewedAt = null;
  }

  const response = await http.patch(`${BASE_PATH}/${id}`, updatePayload);
  return normalizeCompanyDocument(response.data);
}

export async function deleteCompanyDocument(id) {
  if (!id) {
    return;
  }
  await http.delete(`${BASE_PATH}/${id}`);
}
